/**
 * rb-render.js — THE single shared resume render function.
 *
 * RbRender.render(model, opts) -> semantic HTML string. Pure, DOM-free.
 *
 * This one function drives the on-screen preview, the template-gallery
 * thumbnails AND the full-design PDF (print iframe). They can therefore never
 * diverge. The DOCX / ATS-PDF exporters and the ATS scorer read the structured
 * ResumeModel directly (they do NOT parse this HTML), so content is consistent
 * between the styled output and the ATS-plain output.
 *
 * Output: a fixed 794px-wide ".rb-doc" (A4 width at 96dpi) with inline styles
 * so it renders identically wherever it is placed.
 *
 * Depends on leaves only: RbI18n, RbTemplates, RbFonts.
 * globalThis.RbRender
 */
(function () {
    'use strict';

    var T = function (k, v) { return globalThis.RbI18n.t(k, v); };
    /* labels printed on the page follow the resume's own language (model.meta.lang) */
    var _cur = null;
    var D = function (k, v) { return globalThis.RbI18n.docT ? globalThis.RbI18n.docT(k, _cur, v) : T(k, v); };

    function esc(s) {
        if (s === undefined || s === null) return '';
        return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
            .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    function nl2br(s) { return esc(s).replace(/\n/g, '<br>'); }
    function lines(s) {
        return String(s || '').split('\n').map(function (l) { return l.trim(); })
            .filter(Boolean);
    }
    function has(arr) { return Array.isArray(arr) && arr.length > 0; }

    /* density -> spacing scale (px) */
    function spacing(density) {
        if (density === 'compact') return { page: 30, sec: 11, item: 7, line: 1.42, name: 22, h2: 12.5, body: 12 };
        if (density === 'airy')    return { page: 48, sec: 22, item: 12, line: 1.62, name: 26, h2: 14, body: 13 };
        return { page: 40, sec: 16, item: 9, line: 1.52, name: 24, h2: 13.5, body: 12.5 };
    }

    /* h2 section-heading style per template sectionStyle knob */
    function h2Style(style, c, sp) {
        var base = 'font-weight:700;letter-spacing:.5px;margin:' + sp.sec + 'px 0 ' + Math.round(sp.item * 0.8) + 'px;font-size:' + sp.h2 + 'px;';
        switch (style) {
            case 'plain':
                return base + 'color:' + c.heading + ';';
            case 'rule':
                return base + 'color:' + c.accent + ';padding-bottom:3px;border-bottom:1px solid ' + c.border + ';';
            case 'caps':
                return base + 'color:' + c.heading + ';text-transform:uppercase;letter-spacing:1.6px;font-size:' + (sp.h2 - 1) + 'px;';
            case 'boxed':
                return base + 'color:#fff;background:' + c.accent + ';display:inline-block;padding:3px 10px;border-radius:3px;';
            default: /* underline */
                return base + 'color:' + c.accent + ';text-transform:uppercase;letter-spacing:1.4px;padding-bottom:4px;border-bottom:2px solid ' + c.border + ';';
        }
    }

    function h2(text, ctx) {
        return '<h2 style="' + h2Style(ctx.sectionStyle, ctx.c, ctx.sp) + 'break-after:avoid;page-break-after:avoid">' + esc(text) + '</h2>';
    }

    /* ---- header / contact ---------------------------------------------- */

    function contactParts(p) {
        var out = [];
        ['email', 'phone', 'address', 'city', 'website', 'linkedin', 'github'].forEach(function (k) {
            if (p[k]) out.push(esc(p[k]));
        });
        return out;
    }

    /* Date of birth as people write it in the resume's language (it is stored
       as the date input's YYYY-MM-DD). */
    function dobText(v) {
        var m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!m) return String(v || '');
        try {
            return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(globalThis.RbI18n.docLang(_cur), { day: 'numeric', month: 'long', year: 'numeric' });
        } catch (e) { return m[3] + '.' + m[2] + '.' + m[1]; }
    }

    /* Raw strings — escaped where they are printed (they used to be escaped
       here AND there: «Trinidad &amp;amp; Tobago»). */
    function personalDetails(p, cult) {
        var parts = [];
        if (cult.dob && p.dob) parts.push(D('date_of_birth') + ': ' + dobText(p.dob));
        if (cult.marital && p.marital) parts.push(D('marital_' + p.marital));
        if (cult.nationality && p.nationality) parts.push(p.nationality);
        if (cult.religion && p.religion) parts.push(p.religion);
        if (cult.gender && p.gender) parts.push(D('gender_' + p.gender));
        return parts;
    }

    /* What the person wants from the job (hh.ru asks for each of these; salary,
       schedule and licence are usual on a European CV too). */
    var PREF_ORDER = ['salary', 'employment', 'schedule', 'relocation', 'travel', 'citizenship', 'license'];
    function prefPairs(model) {
        var pr = model.prefs || {};
        return PREF_ORDER.filter(function (k) { return String(pr[k] || '').trim(); }).map(function (k) {
            return [D('pref_' + k), String(pr[k]).trim()];
        });
    }
    function prefsBlock(model, ctx, onDark) {
        var pairs = prefPairs(model);
        if (!pairs.length) return '';
        var c = ctx.c, sp = ctx.sp;
        var lab = onDark ? 'rgba(255,255,255,.65)' : c.muted;
        var ink = onDark ? '#fff' : c.text;
        return '<div data-rb-section="prefs" style="display:flex;flex-wrap:wrap;gap:3px 16px;font-size:' + (sp.body - 0.5) + 'px;line-height:1.5;margin:0 0 ' + sp.sec + 'px;color:' + ink + '">' +
            pairs.map(function (p) {
                return '<span><span style="color:' + lab + '">' + esc(p[0]) + ':</span> ' + esc(p[1]) + '</span>';
            }).join('') + '</div>';
    }

    /* optional portfolio QR — a plain <img> (data-URL prepared by the
       controller via rb-qr.js, so the renderer stays pure). */
    function qrEl(model, centered) {
        var q = model.qr;
        if (!q || !q.include || !q.dataUrl) return '';
        return '<img src="' + esc(q.dataUrl) + '" alt="QR" style="width:58px;height:58px;' +
            'image-rendering:pixelated;margin-top:7px;' + (centered ? 'margin-inline:auto;' : '') + 'display:block">';
    }

    function photoImg(model, ctx, w) {
        var p = model.personal;
        if (!ctx.cultural.photo || !p.photo) return '';
        var h = Math.round(w * 1.28);
        var radius = ctx.tpl.photoShape === 'round' ? '50%' : '4px';
        return '<img src="' + esc(p.photo) + '" alt="" style="width:' + w + 'px;height:' + h +
            'px;object-fit:cover;border-radius:' + radius + ';flex-shrink:0">';
    }

    /* The name/title/contact header. `mode`: centered|left|split|band|photo-block|compact */
    function renderHeader(model, ctx) {
        var p = model.personal, c = ctx.c, sp = ctx.sp;
        var name = esc(p.name) || esc(D('your_name_ph'));
        var title = p.title ? '<div style="font-size:' + (sp.body + 2) + 'px;color:' + c.accent + ';margin-top:3px;font-weight:500">' + esc(p.title) + '</div>' : '';
        var contact = contactParts(p);
        var details = personalDetails(p, ctx.cultural);
        var onBand = ctx.header === 'band';
        var contactColor = onBand ? 'rgba(255,255,255,.82)' : c.muted;
        var nameColor = onBand ? '#fff' : c.heading;
        var titleColor = onBand ? 'rgba(255,255,255,.9)' : c.accent;
        var contactHtml = contact.length
            ? '<div style="font-size:' + (sp.body - 0.5) + 'px;color:' + contactColor + ';margin-top:5px;line-height:1.55">' + contact.join(' &nbsp;·&nbsp; ') + '</div>'
            : '';
        var detailsHtml = details.length
            ? '<div style="font-size:' + (sp.body - 1) + 'px;color:' + contactColor + ';margin-top:3px">' + details.map(esc).join(' · ') + '</div>'
            : '';
        var titleBand = onBand ? '<div style="font-size:' + (sp.body + 2) + 'px;color:' + titleColor + ';margin-top:3px;font-weight:500">' + esc(p.title || '') + '</div>' : title;
        var nameBlock = '<h1 style="font-size:' + sp.name + 'px;font-weight:700;margin:0;color:' + nameColor + ';line-height:1.15">' + name + '</h1>' +
            (onBand ? (p.title ? titleBand : '') : title) + contactHtml + detailsHtml +
            qrEl(model, ctx.header === 'centered');
        var photo = photoImg(model, ctx, ctx.header === 'photo-block' ? 92 : 74);

        if (ctx.header === 'centered') {
            return '<header style="text-align:center;margin-bottom:' + sp.sec + 'px">' +
                (photo ? '<div style="display:flex;justify-content:center;margin-bottom:8px">' + photo + '</div>' : '') +
                nameBlock + '</header>';
        }
        if (ctx.header === 'split') {
            return '<header style="display:flex;justify-content:space-between;align-items:flex-end;gap:18px;margin-bottom:' + sp.sec +
                'px;padding-bottom:10px;border-bottom:3px solid ' + c.accent + '"><div>' + nameBlock + '</div>' + photo + '</header>';
        }
        if (ctx.header === 'compact') {
            return '<header style="display:flex;align-items:center;gap:14px;margin-bottom:' + sp.sec + 'px">' +
                photo + '<div>' + nameBlock + '</div></header>';
        }
        if (ctx.header === 'photo-block') {
            return '<header style="display:flex;align-items:center;gap:18px;margin-bottom:' + sp.sec + 'px;' +
                'padding-bottom:12px;border-bottom:2px solid ' + c.border + '">' + photo +
                '<div>' + nameBlock + '</div></header>';
        }
        /* left */
        return '<header style="display:flex;align-items:center;gap:16px;margin-bottom:' + sp.sec + 'px">' +
            photo + '<div>' + nameBlock + '</div></header>';
    }

    /* The colored band header (for layout 'header-band'). */
    function renderBand(model, ctx) {
        var c = ctx.c;
        return '<div style="background:' + c.heading + ';padding:26px ' + ctx.sp.page + 'px;">' +
            renderHeader(model, Object.assign({}, ctx, { header: 'band', headerInBand: true })) + '</div>';
    }

    /* ---- content section blocks ---------------------------------------- */

    function entryHead(left, right, c, sp) {
        return '<div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px">' +
            '<strong style="font-size:' + (sp.body + 0.5) + 'px;color:' + c.heading + '">' + left + '</strong>' +
            (right ? '<span style="font-size:' + (sp.body - 1) + 'px;color:' + c.muted + ';white-space:nowrap">' + right + '</span>' : '') +
            '</div>';
    }

    function summaryBlock(model, ctx) {
        if (!model.summary) return '';
        return '<p style="font-size:' + ctx.sp.body + 'px;line-height:' + ctx.sp.line + ';margin:0;color:' + ctx.c.text + '">' +
            nl2br(model.summary) + '</p>';
    }

    function experienceBlock(items, ctx) {
        var c = ctx.c, sp = ctx.sp;
        return items.map(function (x) {
            var range = globalThis.RbI18n.dateRange(x.startDate, x.endDate, x.current, ctx.dateFormat, D('present'));
            var sub = [esc(x.company), esc(x.location)].filter(Boolean).join(', ');
            var ach = lines(x.achievements);
            return '<div style="margin-bottom:' + sp.item + 'px;break-inside:avoid;page-break-inside:avoid">' +
                entryHead(esc(x.position), range, c, sp) +
                (sub ? '<div style="font-size:' + (sp.body - 0.5) + 'px;color:' + c.accent + ';margin-top:1px">' + sub + '</div>' : '') +
                (x.description ? '<p style="font-size:' + sp.body + 'px;line-height:' + sp.line + ';margin:3px 0 0;color:' + c.text + '">' + nl2br(x.description) + '</p>' : '') +
                (ach.length ? '<ul style="margin:3px 0 0;padding-inline-start:16px;font-size:' + sp.body + 'px;line-height:' + sp.line + ';color:' + c.text + '">' +
                    ach.map(function (a) { return '<li>' + esc(a) + '</li>'; }).join('') + '</ul>' : '') +
                '</div>';
        }).join('');
    }

    function educationBlock(items, ctx) {
        var c = ctx.c, sp = ctx.sp;
        return items.map(function (x) {
            var range = globalThis.RbI18n.dateRange(x.startDate, x.endDate, false, ctx.dateFormat, '');
            var deg = [esc(x.degree), esc(x.field)].filter(Boolean).join(' — ');
            var sub = [esc(x.institution),
                       x.gpa ? D('gpa') + ': ' + esc(x.gpa) : '',
                       esc(x.honors)].filter(Boolean).join(' · ');
            return '<div style="margin-bottom:' + sp.item + 'px;break-inside:avoid;page-break-inside:avoid">' +
                entryHead(deg || esc(x.institution), range, c, sp) +
                (sub && deg ? '<div style="font-size:' + sp.body + 'px;color:' + c.text + ';margin-top:1px">' + sub + '</div>' : '') +
                '</div>';
        }).join('');
    }

    var SKILL_PCT = { beginner: 25, elementary: 45, intermediate: 65, advanced: 82, expert: 100 };

    function skillsBlock(items, ctx, onDark) {
        var c = ctx.c, sp = ctx.sp;
        var ink = onDark ? '#fff' : c.text;
        var accent = onDark ? '#fff' : c.accent;
        if (ctx.skillStyle === 'bar') {
            return '<div style="display:grid;grid-template-columns:1fr 1fr;gap:5px 14px">' + items.map(function (s) {
                var pct = SKILL_PCT[s.level] || 65;
                var track = onDark ? 'rgba(255,255,255,.22)' : c.border;
                return '<div style="font-size:' + (sp.body - 0.5) + 'px;color:' + ink + '">' + esc(s.name) +
                    '<div style="height:4px;border-radius:2px;background:' + track + ';margin-top:2px">' +
                    '<div style="height:100%;width:' + pct + '%;border-radius:2px;background:' + accent + '"></div></div></div>';
            }).join('') + '</div>';
        }
        if (ctx.skillStyle === 'chip') {
            var chipBg = onDark ? 'rgba(255,255,255,.16)' : c.subtle;
            var chipBd = onDark ? 'rgba(255,255,255,.28)' : c.border;
            var chipInk = onDark ? '#fff' : c.accent;
            return '<div style="display:flex;flex-wrap:wrap;gap:5px">' + items.map(function (s) {
                return '<span style="font-size:' + (sp.body - 0.5) + 'px;padding:2px 9px;border-radius:11px;background:' + chipBg +
                    ';color:' + chipInk + ';border:1px solid ' + chipBd + '">' + esc(s.name) + '</span>';
            }).join('') + '</div>';
        }
        /* text */
        return '<div style="font-size:' + sp.body + 'px;line-height:' + sp.line + ';color:' + ink + '">' +
            items.map(function (s) { return esc(s.name); }).join(' &nbsp;·&nbsp; ') + '</div>';
    }

    function langLabel(p) {
        var map = { a1: 'A1', a2: 'A2', b1: 'B1', b2: 'B2', c1: 'C1', c2: 'C2', native: D('prof_native') };
        return map[p] || p;
    }
    function languagesBlock(items, ctx, onDark) {
        var ink = onDark ? '#fff' : ctx.c.text;
        var mut = onDark ? 'rgba(255,255,255,.65)' : ctx.c.muted;
        return '<div style="font-size:' + ctx.sp.body + 'px;line-height:' + ctx.sp.line + ';color:' + ink + '">' +
            items.map(function (l) {
                return esc(l.name) + ' <span style="color:' + mut + '">(' + esc(langLabel(l.proficiency)) + ')</span>';
            }).join(' &nbsp;·&nbsp; ') + '</div>';
    }

    function simpleEntries(items, fields, ctx) {
        var c = ctx.c, sp = ctx.sp;
        return items.map(function (x) {
            var parts = fields.map(function (f) { return x[f] ? esc(x[f]) : ''; }).filter(Boolean);
            return '<div style="font-size:' + sp.body + 'px;line-height:' + sp.line + ';color:' + c.text + ';margin-bottom:3px">' +
                parts.join(' — ') + '</div>';
        }).join('');
    }

    function projectsBlock(items, ctx) {
        var c = ctx.c, sp = ctx.sp;
        return items.map(function (x) {
            return '<div style="margin-bottom:' + Math.round(sp.item * 0.8) + 'px;break-inside:avoid;page-break-inside:avoid">' +
                entryHead(esc(x.name), esc(x.url || ''), c, sp) +
                (x.description ? '<p style="font-size:' + sp.body + 'px;line-height:' + sp.line + ';margin:2px 0 0;color:' + c.text + '">' + nl2br(x.description) + '</p>' : '') +
                (x.technologies ? '<div style="font-size:' + (sp.body - 1) + 'px;color:' + c.accent + ';margin-top:2px">' + esc(x.technologies) + '</div>' : '') +
                '</div>';
        }).join('');
    }

    function volunteerBlock(items, ctx) {
        var c = ctx.c, sp = ctx.sp;
        return items.map(function (x) {
            var range = globalThis.RbI18n.dateRange(x.startDate, x.endDate, false, ctx.dateFormat, '');
            return '<div style="margin-bottom:' + Math.round(sp.item * 0.8) + 'px;break-inside:avoid;page-break-inside:avoid">' +
                entryHead(esc(x.role), range, c, sp) +
                (x.organization ? '<div style="font-size:' + (sp.body - 0.5) + 'px;color:' + c.accent + '">' + esc(x.organization) + '</div>' : '') +
                (x.description ? '<p style="font-size:' + sp.body + 'px;line-height:' + sp.line + ';margin:2px 0 0;color:' + c.text + '">' + nl2br(x.description) + '</p>' : '') +
                '</div>';
        }).join('');
    }

    function referencesBlock(items, ctx) {
        var c = ctx.c, sp = ctx.sp;
        return items.map(function (x) {
            var l1 = [esc(x.name), esc(x.title)].filter(Boolean).join(', ');
            var l2 = [esc(x.company), esc(x.contact)].filter(Boolean).join(' · ');
            return '<div style="font-size:' + sp.body + 'px;line-height:' + sp.line + ';color:' + c.text + ';margin-bottom:4px">' +
                '<strong>' + l1 + '</strong>' + (l2 ? '<br>' + l2 : '') + '</div>';
        }).join('');
    }

    function customBlocks(model, ctx) {
        return (model.custom || []).filter(function (cs) {
            return cs.title || (cs.items || []).some(Boolean);
        }).map(function (cs) {
            return '<section data-rb-section="custom">' + h2(cs.title || D('section_custom'), ctx) +
                '<div style="font-size:' + ctx.sp.body + 'px;line-height:' + ctx.sp.line + ';color:' + ctx.c.text + '">' +
                (cs.items || []).filter(Boolean).map(function (i) { return '<div>' + nl2br(i) + '</div>'; }).join('') +
                '</div></section>';
        }).join('');
    }

    /* signature block (German Lebenslauf convention) */
    function signatureBlock(model, ctx) {
        if (!ctx.cultural.signature) return '';
        var c = ctx.c, sp = ctx.sp;
        return '<div style="margin-top:' + (sp.sec + 10) + 'px;display:flex;justify-content:space-between;' +
            'font-size:' + sp.body + 'px;color:' + c.muted + '">' +
            '<span>' + esc(model.personal.city || model.personal.address || '') + '</span>' +
            '<span style="border-top:1px solid ' + c.muted + ';padding-top:3px;min-width:160px;text-align:center">' +
            esc(model.personal.name || '') + '</span></div>';
    }

    /* Render one content section by id. Returns '' if the section is empty. */
    function renderSection(id, model, ctx) {
        var body = '';
        switch (id) {
            case 'summary':
                if (!model.summary) return '';
                return '<section data-rb-section="summary">' + h2(D('section_summary'), ctx) + summaryBlock(model, ctx) + '</section>';
            case 'experience':
                if (!has(model.experience)) return '';
                body = experienceBlock(model.experience, ctx); break;
            case 'education':
                if (!has(model.education)) return '';
                body = educationBlock(model.education, ctx); break;
            case 'skills':
                if (!has(model.skills)) return '';
                body = skillsBlock(model.skills, ctx, false); break;
            case 'languages':
                if (!has(model.languages)) return '';
                body = languagesBlock(model.languages, ctx, false); break;
            case 'certifications':
                if (!has(model.certifications)) return '';
                body = simpleEntries(model.certifications, ['name', 'issuer', 'date'], ctx); break;
            case 'projects':
                if (!has(model.projects)) return '';
                body = projectsBlock(model.projects, ctx); break;
            case 'awards':
                if (!has(model.awards)) return '';
                body = simpleEntries(model.awards, ['title', 'issuer', 'date'], ctx); break;
            case 'publications':
                if (!has(model.publications)) return '';
                body = simpleEntries(model.publications, ['title', 'publisher', 'date'], ctx); break;
            case 'volunteer':
                if (!has(model.volunteer)) return '';
                body = volunteerBlock(model.volunteer, ctx); break;
            case 'references':
                if (!has(model.references)) return '';
                body = referencesBlock(model.references, ctx); break;
            default:
                return '';
        }
        return '<section data-rb-section="' + id + '">' +
            h2(D('section_' + id), ctx) + body + '</section>';
    }

    /* ---- layout assemblers --------------------------------------------- */

    var SIDEBAR_SECTIONS = ['skills', 'languages', 'certifications'];

    function mainOrder(model) {
        /* content sections in user order, excluding personal */
        return (model.meta.sectionOrder || []).filter(function (s) { return s !== 'personal'; });
    }

    function layoutSingle(model, ctx, banded) {
        var inner = mainOrder(model).map(function (id) { return renderSection(id, model, ctx); }).join('') +
            customBlocks(model, ctx) + signatureBlock(model, ctx);
        if (banded) {
            return renderBand(model, ctx) +
                '<div style="padding:' + Math.round(ctx.sp.page * 0.62) + 'px ' + ctx.sp.page + 'px">' + prefsBlock(model, ctx) + inner + '</div>';
        }
        return '<div style="padding:' + ctx.sp.page + 'px">' + renderHeader(model, ctx) + prefsBlock(model, ctx) + inner + '</div>';
    }

    function layoutSidebar(model, ctx, side) {
        var c = ctx.c;
        var sideCtx = Object.assign({}, ctx, { sectionStyle: 'caps', onDark: true });
        var sideInner = SIDEBAR_SECTIONS.map(function (id) {
            if (id === 'skills' && has(model.skills)) {
                return '<div style="margin-bottom:14px"><div style="' + h2Style('caps', { heading: '#fff', accent: '#fff', border: 'rgba(255,255,255,.3)' }, ctx.sp) + 'color:rgba(255,255,255,.92)">' + esc(D('section_skills')) + '</div>' + skillsBlock(model.skills, ctx, true) + '</div>';
            }
            if (id === 'languages' && has(model.languages)) {
                return '<div style="margin-bottom:14px"><div style="' + h2Style('caps', {}, ctx.sp) + 'color:rgba(255,255,255,.92)">' + esc(D('section_languages')) + '</div>' + languagesBlock(model.languages, ctx, true) + '</div>';
            }
            if (id === 'certifications' && has(model.certifications)) {
                return '<div style="margin-bottom:14px"><div style="' + h2Style('caps', {}, ctx.sp) + 'color:rgba(255,255,255,.92)">' + esc(D('section_certifications')) + '</div>' +
                    '<div style="font-size:' + ctx.sp.body + 'px;line-height:' + ctx.sp.line + ';color:rgba(255,255,255,.85)">' +
                    model.certifications.map(function (x) { return '<div>' + esc(x.name) + (x.issuer ? ' — ' + esc(x.issuer) : '') + '</div>'; }).join('') + '</div></div>';
            }
            return '';
        }).join('');

        var p = model.personal;
        var photo = photoImg(model, Object.assign({}, ctx, { tpl: ctx.tpl }), 96);
        var contact = contactParts(p);
        var sidebar = '<div style="background:' + c.heading + ';color:#fff;padding:26px 20px">' +
            (photo ? '<div style="text-align:center;margin-bottom:12px">' + photo + '</div>' : '') +
            '<h1 style="font-size:19px;font-weight:700;margin:0;color:#fff;text-align:center">' + (esc(p.name) || D('your_name_ph')) + '</h1>' +
            (p.title ? '<div style="font-size:12.5px;color:rgba(255,255,255,.8);text-align:center;margin-top:3px">' + esc(p.title) + '</div>' : '') +
            (contact.length ? '<div style="font-size:12px;color:rgba(255,255,255,.82);margin-top:14px;line-height:1.8;word-break:break-word">' +
                contact.map(function (x) { return '<div>' + x + '</div>'; }).join('') + '</div>' : '') +
            qrEl(model, true) +
            '<div style="margin-top:16px">' + prefsBlock(model, ctx, true) + sideInner + '</div></div>';

        var mainSections = mainOrder(model).filter(function (s) {
            return SIDEBAR_SECTIONS.indexOf(s) < 0;
        });
        var main = '<div style="padding:26px 28px">' +
            mainSections.map(function (id) { return renderSection(id, model, ctx); }).join('') +
            customBlocks(model, ctx) + signatureBlock(model, ctx) + '</div>';

        var cols = side === 'right' ? (main + sidebar) : (sidebar + main);
        var grid = side === 'right' ? '1fr 248px' : '248px 1fr';
        return '<div style="display:grid;grid-template-columns:' + grid + ';min-height:' + ctx.pageH + 'px">' + cols + '</div>';
    }

    function layoutTwoColumn(model, ctx) {
        var sec = mainOrder(model);
        var primary = ['summary', 'experience', 'education', 'projects', 'publications', 'volunteer'];
        var left = sec.filter(function (s) { return primary.indexOf(s) > -1; });
        var right = sec.filter(function (s) { return primary.indexOf(s) < 0; });
        var lHtml = left.map(function (id) { return renderSection(id, model, ctx); }).join('') + customBlocks(model, ctx);
        var rHtml = right.map(function (id) { return renderSection(id, model, ctx); }).join('');
        return '<div style="padding:' + ctx.sp.page + 'px">' + renderHeader(model, ctx) + prefsBlock(model, ctx) +
            '<div style="display:grid;grid-template-columns:1.55fr 1fr;gap:22px">' +
            '<div>' + lHtml + '</div><div>' + rHtml + signatureBlock(model, ctx) + '</div></div></div>';
    }

    function layoutTabular(model, ctx) {
        var c = ctx.c, sp = ctx.sp;
        function row(label, value) {
            return '<tr><td style="padding:5px 9px;border:1px solid ' + c.border + ';background:' + c.subtle +
                ';font-size:' + (sp.body - 0.5) + 'px;color:' + c.heading + ';font-weight:600;width:150px;vertical-align:top">' + esc(label) + '</td>' +
                '<td style="padding:5px 9px;border:1px solid ' + c.border + ';font-size:' + sp.body + 'px;color:' + c.text + '">' + value + '</td></tr>';
        }
        var p = model.personal;
        var infoRows = '';
        infoRows += row(D('full_name'), esc(p.name));
        if (p.title) infoRows += row(D('job_title'), esc(p.title));
        if (contactParts(p).length) infoRows += row(D('contacts'), contactParts(p).join(' · '));
        var det = personalDetails(p, ctx.cultural);
        prefPairs(model).forEach(function (pp) { infoRows += row(pp[0], esc(pp[1])); });
        if (det.length) infoRows += row(D('section_personal'), det.map(esc).join(' · '));

        function tableSection(id, rows) {
            if (!rows) return '';
            return '<section data-rb-section="' + id + '">' + h2(D('section_' + id), ctx) +
                '<table style="width:100%;border-collapse:collapse">' + rows + '</table></section>';
        }
        var eduRows = has(model.education) ? model.education.map(function (e) {
            return row(globalThis.RbI18n.dateRange(e.startDate, e.endDate, false, ctx.dateFormat, ''),
                [esc(e.institution), esc(e.degree), esc(e.field)].filter(Boolean).join(' — '));
        }).join('') : '';
        var expRows = has(model.experience) ? model.experience.map(function (x) {
            return row(globalThis.RbI18n.dateRange(x.startDate, x.endDate, x.current, ctx.dateFormat, D('present')),
                [esc(x.company), esc(x.position)].filter(Boolean).join(' — ') +
                (x.description ? '<div style="color:' + c.muted + ';margin-top:2px">' + nl2br(x.description) + '</div>' : ''));
        }).join('') : '';

        var rest = mainOrder(model).filter(function (s) {
            return ['summary', 'experience', 'education'].indexOf(s) < 0;
        }).map(function (id) { return renderSection(id, model, ctx); }).join('');

        return '<div style="padding:' + sp.page + 'px">' +
            renderHeader(model, Object.assign({}, ctx, { header: 'photo-block' })) +
            '<section style="break-inside:avoid"><table style="width:100%;border-collapse:collapse">' + infoRows + '</table></section>' +
            (model.summary ? '<section data-rb-section="summary">' + h2(D('section_summary'), ctx) + summaryBlock(model, ctx) + '</section>' : '') +
            tableSection('education', eduRows) +
            tableSection('experience', expRows) +
            rest + customBlocks(model, ctx) + signatureBlock(model, ctx) + '</div>';
    }

    /* ---- public render ------------------------------------------------- */

    /* The assistant leaves "[N%]"-style placeholders for the person to fill in.
       On the screen they are highlighted on the page itself, so what is left to
       do is visible where it will print; files never carry the mark (the export
       warns instead). Text was escaped before, so a bracket can only come from
       text content — attributes here never contain one. */
    var MARK_RE = /\[([^\]<>"\n]{1,80})\]/g;
    function markPlaceholders(html) {
        return html.replace(MARK_RE, '<mark data-rb-ph style="background:#fde68a;color:#5b4300;border-radius:2px;padding:0 2px">[$1]</mark>');
    }

    function render(model, opts) {
        opts = opts || {};
        _cur = model;
        var tpl = globalThis.RbTemplates.get(model.meta.template);
        var c = globalThis.RbI18n.getScheme(model.meta.scheme);
        var sp = spacing(tpl.density);
        var locale = globalThis.RbI18n.docLang ? globalThis.RbI18n.docLang(model) : globalThis.RbI18n.LOCALE;
        var rtl = !!opts.rtl;
        var fontStack = globalThis.RbFonts.screenStack(tpl.font, locale);
        var pg = globalThis.RbState.pageSize(model.meta.pageSize);

        var ctx = {
            tpl: tpl, c: c, sp: sp, pageW: pg.w, pageH: pg.h,
            cultural: model.cultural || {},
            dateFormat: opts.dateFormat || 'MM/YYYY',
            sectionStyle: tpl.sectionStyle,
            header: tpl.header,
            skillStyle: tpl.skillStyle,
            accentMode: tpl.accentMode,
            rtl: rtl
        };

        var inner;
        switch (tpl.layout) {
            case 'header-band':   inner = layoutSingle(model, ctx, true); break;
            case 'sidebar-left':  inner = layoutSidebar(model, ctx, 'left'); break;
            case 'sidebar-right': inner = layoutSidebar(model, ctx, 'right'); break;
            case 'two-column':    inner = layoutTwoColumn(model, ctx); break;
            case 'tabular':       inner = layoutTabular(model, ctx); break;
            case 'grid':          inner = layoutTabular(model, ctx); break; /* rirekisho ≈ tabular grid */
            default:              inner = layoutSingle(model, ctx, false);
        }

        var rootStyle = 'width:' + pg.w + 'px;min-height:' + pg.h + 'px;background:#fff;color:' + c.text +
            ';font-family:' + fontStack + ';font-size:' + sp.body + 'px;line-height:' + sp.line +
            ';box-sizing:border-box;overflow:hidden;' + (rtl ? 'direction:rtl;text-align:right;' : '');

        if (opts.marks) inner = markPlaceholders(inner);
        var lang = globalThis.RbI18n.docLang ? globalThis.RbI18n.docLang(model) : locale;
        return '<div class="rb-doc" lang="' + esc(lang) + '" data-rb-layout="' + esc(tpl.layout || 'single') + '" data-rb-template="' + esc(tpl.id) + '" data-rb-target="' +
            esc(opts.target || 'preview') + '" dir="' + (rtl ? 'rtl' : 'ltr') + '" style="' + rootStyle + '">' +
            inner + '</div>';
    }

    globalThis.RbRender = { render: render, prefPairs: function (m) { _cur = m; return prefPairs(m); } };
})();
